import React from "react";
import {
  Alert,
  Button,
  ButtonProps,
  FormField,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OmsExportPreset } from "../../lusca-search-api/generated-src";
import { OmsPresetsState, presetErrorMessage } from "../../hooks/useOmsPresets";
import { presetScopeLabel } from "./omsPresetEditing";

export default function OMSPresetToolbar({
  state,
  applied,
  disabled,
  saveDisabled = disabled,
  onApply,
  onSave,
  onManage,
  saveRef,
  selectRef,
  manageRef,
}: {
  state: OmsPresetsState;
  applied?: OmsExportPreset;
  disabled: boolean;
  saveDisabled?: boolean;
  onApply: (presetId: string) => void;
  onSave: () => void;
  onManage: () => void;
  saveRef: React.RefObject<ButtonProps.Ref>;
  selectRef: React.RefObject<SelectProps.Ref>;
  manageRef: React.RefObject<ButtonProps.Ref>;
}) {
  return (
    <SpaceBetween size="s">
      <div className="oms-preset-toolbar">
        <FormField label="Use a saved preset">
          <Select
            expandToViewport
            ref={selectRef}
            ariaLabel="Saved preset"
            placeholder="Choose a preset"
            filteringType="auto"
            filteringPlaceholder="Search presets"
            filteringAriaLabel="Search presets"
            filteringClearAriaLabel="Clear preset search"
            empty="No saved presets for this product."
            noMatch="No matching presets."
            statusType={
              state.list.isLoading || state.read.isLoading
                ? "loading"
                : "finished"
            }
            loadingText={
              state.read.isLoading ? "Applying preset" : "Loading presets"
            }
            disabled={disabled || state.read.isLoading}
            selectedOption={
              applied
                ? {
                    value: applied.presetId,
                    label: applied.name,
                    description: presetScopeLabel[applied.scope],
                  }
                : null
            }
            options={(state.list.data || []).map((preset) => ({
              value: preset.presetId,
              label: preset.name,
              description: presetScopeLabel[preset.scope],
            }))}
            onChange={({ detail }) =>
              onApply(detail.selectedOption.value as string)
            }
          />
        </FormField>
        <div className="oms-preset-toolbar__actions">
          {(!applied || disabled) && (
            <Button
              ref={saveRef}
              disabled={saveDisabled || state.read.isLoading}
              onClick={onSave}
            >
              Save as preset
            </Button>
          )}
          <Button
            ref={manageRef}
            ariaLabel="Manage presets"
            disabled={disabled || state.read.isLoading}
            onClick={onManage}
          >
            Manage presets
          </Button>
        </div>
      </div>
      {state.list.isError && (
        <Alert
          type="warning"
          header="Presets unavailable"
          action={
            <Button
              onClick={() => {
                state.list.refetch().catch(() => undefined);
              }}
            >
              Retry presets
            </Button>
          }
        >
          You can still configure and create an export.
        </Alert>
      )}
      {state.read.isError && (
        <Alert
          type="error"
          header="Could not apply preset"
          action={
            <Button
              onClick={() => {
                if (state.read.variables) onApply(state.read.variables);
              }}
            >
              Retry preset
            </Button>
          }
        >
          {presetErrorMessage(state.read.error)}
        </Alert>
      )}
    </SpaceBetween>
  );
}
